import { e2eeRoomId, e2eeUserId, userIdFromE2ee, type DeviceDto, type MegolmContent, type MessageDto } from '@dijitalapp/shared';
import type * as CryptoModule from '@matrix-org/matrix-sdk-crypto-wasm';
import { api, ApiError } from './api';
import type { E2eeFileMeta } from './outbox';

/**
 * Browser side of end-to-end encryption (ADR-014). All cryptography happens inside matrix-sdk-crypto
 * (vodozemac, compiled to WebAssembly); this module moves its requests to our key-distribution API,
 * feeds the answers back, and drives the user-facing flows (identity, verification, backup).
 *
 * Trust model: each person has a cross-signing identity (master key) that signs their devices. Room keys
 * are shared only with devices signed by their owner's identity; messages are only accepted from such
 * devices. People are "verified" by comparing emoji or a safety code, which signs their identity.
 */

type Crypto = typeof CryptoModule;
type Machine = CryptoModule.OlmMachine;
type Outgoing = { id?: string; type: number; body: string; event_type?: string; txn_id?: string; version?: string };

const PASS_KEY = (deviceId: string) => `dijitalapp-e2ee-pass:${deviceId}`;
const SINCE_KEY = (deviceId: string) => `dijitalapp-e2ee-since:${deviceId}`;
const BACKUP_SECRET = 'm.megolm_backup.v1';
const BACKUP_ALGORITHM = 'm.megolm_backup.v1.curve25519-aes-sha2';

function storeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function storeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the store then only lives for this tab */
  }
}

/**
 * Passphrase for the library's encrypted IndexedDB store. It lives in the same browser profile, so it
 * only protects against raw copies of the database, not against someone who controls this browser.
 */
function storePassphrase(deviceId: string): string {
  let pass = storeGet(PASS_KEY(deviceId));
  if (!pass) {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    pass = btoa(String.fromCharCode(...bytes));
    storeSet(PASS_KEY(deviceId), pass);
  }
  return pass;
}

/** Splits a key into groups of four characters so two people can read it to each other. */
export function formatCode(key: string): string {
  return (key.match(/.{1,4}/g) ?? []).join(' ');
}

export type PeerTrust = 'verified' | 'unverified' | 'changed';
export type VerificationPhase = 'requested' | 'ready' | 'qr-show' | 'qr-scanned' | 'qr-waiting' | 'emoji' | 'confirmed' | 'done' | 'cancelled';

export interface VerificationView {
  flowId: string;
  /** DijitalApp user id of the other side (ourselves for a device-to-device self verification). */
  userId: string;
  self: boolean;
  incoming: boolean;
  phase: VerificationPhase;
  emoji: { symbol: string; description: string }[];
  /** Our QR code while we show it: module grid, `size` × `size`. */
  qr: { size: number; modules: Uint8ClampedArray } | null;
  reason: string | null;
}

interface Flow {
  request: CryptoModule.VerificationRequest;
  sas: CryptoModule.Sas | null;
  /** QR verification: the code we show, or the one we scanned. */
  qr: CryptoModule.Qr | null;
  qrShown: boolean;
  incoming: boolean;
  self: boolean;
  confirmed: boolean;
  finished: boolean;
}

/** Decrypted room event content (`m.text`, `m.file`, `m.image`, `m.audio`, `m.video`). */
export interface DecryptedContent {
  msgtype?: string;
  body?: string;
  /** Reactions point at the message they belong to. */
  'm.relates_to'?: { rel_type?: string; event_id?: string };
  info?: { mimetype?: string; size?: number; duration?: number };
  file?: Record<string, unknown> & { url?: string };
}
export interface DecryptedMessage {
  content: DecryptedContent;
  unverifiedSender: boolean;
}

/** Attachment ids travel as Matrix content URIs inside the encrypted content. */
export const fileUri = (attachmentId: string) => `mxc://dijitalapp.com/${attachmentId}`;
export const attachmentIdOf = (uri: string | undefined) => (uri?.startsWith('mxc://dijitalapp.com/') ? uri.slice('mxc://dijitalapp.com/'.length) : null);

export class E2ee {
  private chain: Promise<unknown> = Promise.resolve();
  private since: string | undefined;
  private plaintext = new Map<string, Promise<DecryptedMessage | null>>();
  /** Recipients of the current group session per conversation; any departure starts a new session. */
  private sessionMembers = new Map<string, Set<string>>();
  private listeners = new Set<() => void>();
  private flows = new Map<string, Flow>();
  private watched = new Set<string>();
  /** Bumped on every change the UI shows (new keys, trust, verification progress). */
  version = 0;
  /** This device is signed by its owner's identity (can read and be trusted). */
  deviceVerified = false;
  /** A newly created recovery key, to be shown once and then forgotten by the UI. */
  recoveryKeyToShow: string | null = null;

  private constructor(
    private readonly lib: Crypto,
    private readonly machine: Machine,
    readonly userId: string,
    readonly deviceId: string,
  ) {
    this.since = storeGet(SINCE_KEY(deviceId)) ?? undefined;
    this.watched.add(userId);
  }

  static async start(userId: string): Promise<E2ee | null> {
    if (typeof WebAssembly === 'undefined' || typeof indexedDB === 'undefined') return null;
    const { devices } = await api<{ devices: DeviceDto[] }>('GET', '/devices');
    const deviceId = devices.find((d) => d.current)?.id;
    if (!deviceId) return null;
    const lib = await import('@matrix-org/matrix-sdk-crypto-wasm');
    await lib.initAsync();
    const machine = await lib.OlmMachine.initialize(
      new lib.UserId(e2eeUserId(userId)),
      new lib.DeviceId(deviceId),
      `dijitalapp-e2ee:${deviceId}`,
      storePassphrase(deviceId),
    );
    const e = new E2ee(lib, machine, userId, deviceId);
    await e.sync();
    await e.exclusive(() => e.ensureIdentity());
    return e;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private changed() {
    this.version += 1;
    this.listeners.forEach((l) => l());
  }

  /** Runs crypto operations one at a time: the state machine must not process requests concurrently. */
  private exclusive<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.chain.then(fn, fn);
    this.chain = run.catch(() => undefined);
    return run;
  }

  private uid(id: string) {
    return new this.lib.UserId(e2eeUserId(id));
  }

  // ---------- transport ----------

  private async send(r: Outgoing) {
    const { RequestType } = this.lib;
    const body = JSON.parse(r.body) as unknown;
    let response: unknown = {};
    switch (r.type) {
      case RequestType.KeysUpload:
        response = await api('POST', '/e2ee/keys/upload', body);
        break;
      case RequestType.KeysQuery:
        response = await api('POST', '/e2ee/keys/query', body);
        break;
      case RequestType.KeysClaim:
        response = await api('POST', '/e2ee/keys/claim', body);
        break;
      case RequestType.ToDevice:
        response = await api('PUT', `/e2ee/send-to-device/${encodeURIComponent(r.event_type ?? '')}/${encodeURIComponent(r.txn_id ?? '')}`, body);
        break;
      case RequestType.SignatureUpload:
        response = await api('POST', '/e2ee/keys/signatures/upload', body);
        break;
      case RequestType.KeysBackup:
        response = await api('PUT', `/e2ee/room-keys/keys?version=${encodeURIComponent(r.version ?? '')}`, body);
        break;
      default:
        break; // In-room events are not used: verification runs device to device.
    }
    if (r.id) await this.machine.markRequestAsSent(r.id, r.type, JSON.stringify(response ?? {}));
  }

  private async flush() {
    for (const r of await this.machine.outgoingRequests()) await this.send(r as Outgoing);
  }

  /** Pulls pending packets and device-list changes; called on start, on reconnect and on `e2ee.sync`. */
  sync(): Promise<void> {
    return this.exclusive(async () => {
      let received = 0;
      for (;;) {
        const res = await api<{
          next: string;
          more: boolean;
          to_device: { events: unknown[] };
          device_lists: { changed: string[] };
          device_one_time_keys_count: Record<string, number>;
          device_unused_fallback_key_types: string[];
        }>('GET', `/e2ee/sync${this.since ? `?since=${encodeURIComponent(this.since)}` : ''}`);
        await this.machine.receiveSyncChanges(
          JSON.stringify(res.to_device.events),
          new this.lib.DeviceLists(res.device_lists.changed.map((u) => new this.lib.UserId(u))),
          new Map(Object.entries(res.device_one_time_keys_count)),
          new Set(res.device_unused_fallback_key_types),
        );
        // Only acknowledge after the machine has stored the packets.
        this.since = res.next;
        storeSet(SINCE_KEY(this.deviceId), res.next);
        received += res.to_device.events.length + res.device_lists.changed.length;
        await this.flush();
        if (!res.more) break;
      }
      await this.collectIncomingVerifications();
      await this.pumpVerifications();
      await this.receiveBackupSecret();
      await this.refreshDeviceTrust();
      await this.backup();
      if (received > 0) {
        // Messages that failed before may be readable now.
        for (const [id, p] of this.plaintext) void p.then((v) => v === null && this.plaintext.delete(id));
        this.changed();
      }
    });
  }

  // ---------- identity ----------

  /**
   * First device of a person: creates the cross-signing identity and the key backup (whose recovery key
   * is shown once). Later devices stay unverified until verified from another device.
   */
  private async ensureIdentity() {
    await this.machine.updateTrackedUsers([this.uid(this.userId)]);
    await this.flush();
    const own = await this.machine.getIdentity(this.uid(this.userId));
    const status = await this.machine.crossSigningStatus();
    if (!own && !status.hasMaster) {
      await this.bootstrap(false);
      await this.createBackup();
    } else {
      await this.enableExistingBackup();
    }
    await this.refreshDeviceTrust();
    this.changed();
  }

  private async bootstrap(reset: boolean, password?: string) {
    const reqs = await this.machine.bootstrapCrossSigning(reset);
    if (reqs.uploadKeysRequest) await this.send(reqs.uploadKeysRequest as Outgoing);
    const signing = JSON.parse(reqs.uploadSigningKeysRequest.body) as Record<string, unknown>;
    await api('POST', '/e2ee/keys/device-signing/upload', password ? { ...signing, auth: { password } } : signing);
    await api('POST', '/e2ee/keys/signatures/upload', JSON.parse(reqs.uploadSignaturesRequest.body));
    await this.flush();
  }

  private async refreshDeviceTrust() {
    const own = await this.machine.getIdentity(this.uid(this.userId));
    const device = await this.machine.getDevice(this.uid(this.userId), new this.lib.DeviceId(this.deviceId));
    const verified = Boolean(own && device?.isCrossSignedByOwner());
    if (verified !== this.deviceVerified) {
      this.deviceVerified = verified;
      this.changed();
    }
  }

  /** The person's identity key, formatted for comparison ("safety code"); null if they have none yet. */
  async safetyCode(userId: string): Promise<string | null> {
    return this.exclusive(async () => {
      await this.track([userId]);
      const identity = await this.machine.getIdentity(this.uid(userId));
      if (!identity) return null;
      const keys = (JSON.parse(identity.masterKey) as { keys: Record<string, string> }).keys;
      const key = Object.values(keys)[0];
      return key ? formatCode(key) : null;
    });
  }

  async trustOf(userId: string): Promise<PeerTrust> {
    const identity = await this.machine.getIdentity(this.uid(userId));
    if (!identity) return 'unverified';
    if (identity.hasVerificationViolation()) return 'changed';
    if (identity.isVerified()) return 'verified';
    if ('identityNeedsUserApproval' in identity && identity.identityNeedsUserApproval()) return 'changed';
    return 'unverified';
  }

  /** Users whose devices and verification requests we follow (conversation peers). */
  async track(userIds: string[]) {
    const fresh = userIds.filter((u) => !this.watched.has(u));
    fresh.forEach((u) => this.watched.add(u));
    await this.machine.updateTrackedUsers(userIds.map((u) => this.uid(u)));
    await this.flush();
  }

  /** After comparing the safety code in person: signs their identity. */
  verifyByCode(userId: string): Promise<void> {
    return this.exclusive(async () => {
      const identity = await this.machine.getIdentity(this.uid(userId));
      if (!identity) throw new Error('no identity');
      await this.send((await identity.verify()) as Outgoing);
      this.changed();
    });
  }

  /** The person reset their identity: accept the new one (withdrawing an old verification). */
  acceptIdentityChange(userId: string): Promise<void> {
    return this.exclusive(async () => {
      const identity = await this.machine.getIdentity(this.uid(userId));
      if (!identity) return;
      if (identity.hasVerificationViolation()) await identity.withdrawVerification();
      else if ('pinCurrentMasterKey' in identity) await identity.pinCurrentMasterKey();
      this.changed();
    });
  }

  /** Replaces this person's identity (all devices lost). Peers will see an identity change. */
  resetIdentity(password: string): Promise<void> {
    return this.exclusive(async () => {
      await this.bootstrap(true, password);
      await this.createBackup();
      await this.refreshDeviceTrust();
      this.changed();
    });
  }

  // ---------- verification (emoji / SAS, device to device) ----------

  private async collectIncomingVerifications() {
    for (const user of this.watched) {
      for (const request of this.machine.getVerificationRequests(this.uid(user))) {
        if (this.flows.has(request.flowId) || request.weStarted() || request.isDone() || request.isCancelled()) continue;
        this.addFlow(request, true);
      }
    }
  }

  private addFlow(request: Flow['request'], incoming: boolean) {
    const flow: Flow = { request, sas: null, qr: null, qrShown: false, incoming, self: request.isSelfVerification(), confirmed: false, finished: false };
    this.flows.set(request.flowId, flow);
    request.registerChangesCallback(async () => {
      void this.exclusive(() => this.pumpVerifications());
    });
    this.changed();
  }

  /** Advances every flow by one step where the protocol (not the user) decides. */
  private async pumpVerifications() {
    for (const flow of this.flows.values()) {
      const { request } = flow;
      if (flow.finished || request.isCancelled()) continue;
      if (!flow.sas) {
        const v = request.getVerification();
        if (v instanceof this.lib.Sas) {
          flow.sas = v;
          this.watchSas(flow);
          if (!v.weStarted() && !v.hasBeenAccepted()) {
            const accept = v.accept();
            if (accept) await this.send(accept as Outgoing);
          }
        } else if (v instanceof this.lib.Qr && !flow.qr) {
          flow.qr = v;
          this.watchQr(flow);
        }
      }
      if ((request.isDone() || flow.sas?.isDone() || flow.qr?.isDone()) && !flow.finished) {
        flow.finished = true;
        await this.afterVerified(flow);
      }
    }
    this.changed();
  }

  private watchQr(flow: Flow) {
    flow.qr?.registerChangesCallback(async () => {
      void this.exclusive(() => this.pumpVerifications());
    });
  }

  private watchSas(flow: Flow) {
    flow.sas?.registerChangesCallback(async () => {
      void this.exclusive(() => this.pumpVerifications());
    });
  }

  private async afterVerified(flow: Flow) {
    if (flow.self) {
      // Our own new device: fetch the identity's signing keys and the backup key from the verified one.
      await this.machine.requestMissingSecretsIfNeeded();
      await this.flush();
    } else {
      // Their device was verified with emoji; if it is signed by their identity, that identity is
      // authenticated too (the library checks the signature chain), so we sign it.
      const other = flow.request.otherUserId;
      const otherDevice = flow.request.otherDeviceId;
      const device = otherDevice ? await this.machine.getDevice(other, otherDevice) : undefined;
      const identity = await this.machine.getIdentity(other);
      if (device?.isVerified() && device.isCrossSignedByOwner() && identity && !identity.isVerified()) {
        await this.send((await identity.verify()) as Outgoing);
      }
    }
    await this.flush();
    await this.refreshDeviceTrust();
  }

  /** Starts emoji verification with every signed device of a person; the first one to answer wins. */
  verifyUser(userId: string): Promise<void> {
    return this.exclusive(async () => {
      await this.track([userId]);
      const devices = await this.machine.getUserDevices(this.uid(userId));
      for (const device of devices.devices()) {
        if (!device.isCrossSignedByOwner()) continue;
        const [request, out] = device.requestVerification(this.methods());
        await this.send(out as Outgoing);
        this.addFlow(request, false);
      }
    });
  }

  /** Verifies this (new) device with another of our own devices. */
  verifyThisDevice(): Promise<void> {
    return this.exclusive(async () => {
      await this.track([this.userId]);
      const own = await this.machine.getIdentity(this.uid(this.userId));
      if (!own || !('trustsOurOwnDevice' in own)) throw new Error('no identity');
      const [request, out] = await own.requestVerification(this.methods());
      await this.send(out as Outgoing);
      this.addFlow(request, false);
    });
  }

  verifications(): VerificationView[] {
    const views: VerificationView[] = [];
    for (const [flowId, flow] of this.flows) {
      const { request, sas } = flow;
      const qr = flow.qr;
      const phase: VerificationPhase = request.isCancelled() || sas?.isCancelled() || qr?.isCancelled()
        ? 'cancelled'
        : flow.finished
          ? 'done'
          : qr?.hasBeenScanned() && !qr.hasBeenConfirmed()
            ? 'qr-scanned'
            : qr?.reciprocated() || qr?.hasBeenConfirmed()
              ? 'qr-waiting'
              : flow.qrShown
                ? 'qr-show'
                : flow.confirmed
            ? 'confirmed'
            : sas?.emoji()
              ? 'emoji'
              : request.isReady()
                ? 'ready'
                : 'requested';
      views.push({
        flowId,
        userId: userIdFromE2ee(request.otherUserId.toString()) ?? '',
        self: flow.self,
        incoming: flow.incoming,
        phase,
        emoji: (sas?.emoji() ?? []).map((e: CryptoModule.Emoji) => ({ symbol: e.symbol, description: e.description })),
        qr: phase === 'qr-show' && qr ? qrGrid(qr) : null,
        reason: request.cancelInfo?.reason() ?? null,
      });
    }
    return views;
  }

  acceptVerification(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      const out = flow?.request.acceptWithMethods(this.methods());
      if (out) await this.send(out as Outgoing);
      await this.pumpVerifications();
    });
  }

  private methods(): CryptoModule.VerificationMethod[] {
    const m = this.lib.VerificationMethod;
    return [m.SasV1, m.QrCodeShowV1, m.QrCodeScanV1, m.ReciprocateV1];
  }

  /** Compare emoji (either side may start it once the request is accepted). */
  startEmoji(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow || flow.sas) return;
      const started = await flow.request.startSas();
      if (!started) throw new Error('emoji verification could not start');
      flow.sas = started[0];
      flow.qrShown = false;
      await this.send(started[1] as Outgoing);
      this.watchSas(flow);
      await this.pumpVerifications();
    });
  }

  /** Show our QR code for the other device to scan. */
  showQr(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow) return;
      const qr = await flow.request.generateQrCode();
      if (!qr) throw new Error('no QR for this verification');
      flow.qr = qr;
      flow.qrShown = true;
      this.watchQr(flow);
      this.changed();
    });
  }

  /** We scanned the other device's QR code: the library checks it and we tell the other side. */
  scanQr(flowId: string, bytes: Uint8ClampedArray): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow) return;
      const qr = await flow.request.scanQrCode(this.lib.QrCodeScan.fromBytes(bytes));
      flow.qr = qr;
      flow.qrShown = false;
      this.watchQr(flow);
      const out = qr.reciprocate();
      // No reciprocation means the code did not match this verification: stop instead of waiting forever.
      if (!out) {
        const cancel = qr.cancel();
        if (cancel) await this.send(cancel as Outgoing);
        throw new Error('QR code did not match');
      }
      await this.send(out as Outgoing);
      await this.pumpVerifications();
    });
  }

  /** The other device scanned our code and shows success: we confirm, which completes verification. */
  confirmQrScanned(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      const out = flow?.qr?.confirmScanning();
      if (out) await this.send(out as Outgoing);
      await this.pumpVerifications();
    });
  }

  /** The person says the emoji match on both screens. */
  confirmEmoji(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow?.sas) return;
      flow.confirmed = true;
      for (const r of await flow.sas.confirm()) await this.send(r as Outgoing);
      await this.pumpVerifications();
    });
  }

  cancelVerification(flowId: string): Promise<void> {
    return this.exclusive(async () => {
      const flow = this.flows.get(flowId);
      if (!flow) return;
      const out = flow.sas?.cancel() ?? flow.qr?.cancel() ?? flow.request.cancel();
      if (out) await this.send(out as Outgoing);
      this.changed();
    });
  }

  dismissVerification(flowId: string) {
    this.flows.delete(flowId);
    this.changed();
  }

  // ---------- key backup ----------

  private async currentBackup(): Promise<{ version: string; auth_data: { public_key: string } } | null> {
    try {
      return await api('GET', '/e2ee/room-keys/version');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'NOT_FOUND') return null;
      throw err;
    }
  }

  private async createBackup() {
    const key = this.lib.BackupDecryptionKey.createRandomKey();
    const publicKey = key.megolmV1PublicKey.publicKeyBase64;
    const { version } = await api<{ version: string }>('POST', '/e2ee/room-keys/version', {
      algorithm: BACKUP_ALGORITHM,
      auth_data: { public_key: publicKey },
    });
    await this.machine.saveBackupDecryptionKey(key, version);
    await this.machine.enableBackupV1(publicKey, version);
    this.recoveryKeyToShow = key.toBase64();
    await this.backup();
  }

  /** Resumes backing up if this device holds the key of the current backup. */
  private async enableExistingBackup() {
    const info = await this.currentBackup();
    const keys = await this.machine.getBackupKeys();
    const key = keys.decryptionKey;
    if (info && key && key.megolmV1PublicKey.publicKeyBase64 === info.auth_data.public_key) {
      await this.machine.enableBackupV1(info.auth_data.public_key, info.version);
    }
  }

  private async backup() {
    if (!(await this.machine.isBackupEnabled())) return;
    for (let i = 0; i < 50; i++) {
      const r = await this.machine.backupRoomKeys();
      if (!r) return;
      await this.send(r as unknown as Outgoing);
    }
  }

  /** After self-verification the backup key arrives as a secret: keep it and restore history. */
  private async receiveBackupSecret() {
    const secrets = await this.machine.getSecretsFromInbox(BACKUP_SECRET);
    const [secret] = [...secrets];
    if (!secret) return;
    await this.restoreWith(this.lib.BackupDecryptionKey.fromBase64(secret));
    await this.machine.deleteSecretsFromInbox(BACKUP_SECRET);
  }

  private async restoreWith(key: CryptoModule.BackupDecryptionKey): Promise<number> {
    const info = await this.currentBackup();
    if (!info) throw new Error('no backup');
    if (key.megolmV1PublicKey.publicKeyBase64 !== info.auth_data.public_key) throw new Error('wrong recovery key');
    const { rooms } = await api<{
      rooms: Record<string, { sessions: Record<string, { session_data: { ephemeral: string; mac: string; ciphertext: string } }> }>;
    }>('GET', `/e2ee/room-keys/keys?version=${encodeURIComponent(info.version)}`);
    const map = new Map<CryptoModule.RoomId, Map<string, unknown>>();
    for (const [roomId, room] of Object.entries(rooms)) {
      const sessions = new Map<string, unknown>();
      for (const [sid, s] of Object.entries(room.sessions)) {
        try {
          sessions.set(sid, JSON.parse(key.decryptV1(s.session_data.ephemeral, s.session_data.mac, s.session_data.ciphertext)));
        } catch {
          /* one damaged entry must not stop the rest */
        }
      }
      map.set(new this.lib.RoomId(roomId), sessions);
    }
    const result = await this.machine.importBackedUpRoomKeys(map, () => undefined, info.version);
    await this.machine.saveBackupDecryptionKey(key, info.version);
    await this.machine.enableBackupV1(info.auth_data.public_key, info.version);
    this.plaintext.clear();
    this.changed();
    return result.importedCount;
  }

  /** Restores message history on this device from the recovery key. Returns the number of keys restored. */
  restoreFromRecoveryKey(recoveryKey: string): Promise<number> {
    return this.exclusive(() => this.restoreWith(this.lib.BackupDecryptionKey.fromBase64(recoveryKey.replace(/\s+/g, ''))));
  }

  /** The user saved the newly shown recovery key; it is not kept in memory any longer. */
  acknowledgeRecoveryKey() {
    this.recoveryKeyToShow = null;
    this.changed();
  }

  /** The recovery key, if this device holds it (to show again in settings). */
  async recoveryKey(): Promise<string | null> {
    const keys = await this.machine.getBackupKeys();
    return keys.decryptionKey?.toBase64() ?? null;
  }

  // ---------- messages ----------

  /**
   * Encrypts for every signed device of the given people and of this user. Fails if someone we verified
   * changed their identity (the UI then asks the user to re-verify or accept the change).
   */
  encrypt(conversationId: string, peerIds: string[], text: string): Promise<MegolmContent> {
    return this.encryptContent(conversationId, peerIds, { msgtype: 'm.text', body: text });
  }

  /** Content for an encrypted upload: the file key travels inside the encrypted message only. */
  static fileContent(f: E2eeFileMeta): DecryptedContent {
    const msgtype = f.voice ? 'm.audio' : f.mime.startsWith('image/') ? 'm.image' : f.mime.startsWith('video/') ? 'm.video' : f.mime.startsWith('audio/') ? 'm.audio' : 'm.file';
    return {
      msgtype,
      body: f.name,
      info: { mimetype: f.mime, size: f.size, ...(f.durationMs ? { duration: f.durationMs } : {}) },
      file: { ...f.info, url: fileUri(f.attachmentId) },
    };
  }

  encryptContent(conversationId: string, recipientIds: string[], body: DecryptedContent): Promise<MegolmContent> {
    return this.exclusive(async () => {
      const { RoomId, EncryptionSettings, CollectStrategy } = this.lib;
      const ids = [...new Set([this.userId, ...recipientIds])];
      const users = () => ids.map((u) => this.uid(u));
      const room = new RoomId(e2eeRoomId(conversationId));
      // Someone left (or this is the first message since the app started): start a new group session,
      // so a removed member cannot read what follows.
      const previous = this.sessionMembers.get(conversationId);
      if (!previous || [...previous].some((u) => !ids.includes(u))) await this.machine.invalidateGroupSession(room);
      this.sessionMembers.set(conversationId, new Set(ids));
      await this.track(ids);
      const claim = await this.machine.getMissingSessions(users());
      if (claim) await this.send(claim as Outgoing);
      const settings = new EncryptionSettings();
      settings.sharingStrategy = CollectStrategy.identityBasedStrategy();
      let shares;
      try {
        shares = await this.machine.shareRoomKey(room, users(), settings);
      } catch (err) {
        // A half-made session must not be reused (the retry would encrypt with a session whose key this
        // device never stored): start over on the next attempt.
        await this.machine.invalidateGroupSession(room);
        this.sessionMembers.delete(conversationId);
        // The library refuses when someone we verified changed their identity.
        for (const u of ids) if ((await this.trustOf(u)) === 'changed') throw new ApiError('E2EE_IDENTITY_CHANGED', 0);
        throw err;
      }
      for (const r of shares) await this.send(r as Outgoing);
      const content = await this.machine.encryptRoomEvent(room, 'm.room.message', JSON.stringify(body));
      await this.backup();
      return JSON.parse(content) as MegolmContent;
    });
  }

  /**
   * The message text, or null when this device has no key for it (yet). Messages from devices that are
   * not signed by their owner are only shown when the key came from our own backup (history), and are
   * then flagged `unverifiedSender`.
   */
  /** Encrypts a file on this device (AES-CTR with integrity hash, done by the crypto library). */
  encryptFile(bytes: Uint8Array): { data: Uint8Array; info: Record<string, unknown> } {
    const enc = this.lib.Attachment.encrypt(bytes);
    const info = JSON.parse(enc.mediaEncryptionInfo ?? '{}') as Record<string, unknown>;
    return { data: enc.encryptedData, info };
  }

  /** Decrypts a downloaded file; throws if the ciphertext was altered. */
  decryptFile(bytes: Uint8Array, file: Record<string, unknown>): Uint8Array {
    // The library expects the EncryptedFile info without our attachment URI.
    const info = Object.fromEntries(Object.entries(file).filter(([k]) => k !== 'url'));
    return this.lib.Attachment.decrypt(new this.lib.EncryptedAttachment(bytes, JSON.stringify(info)));
  }

  decrypt(m: MessageDto): Promise<DecryptedMessage | null> {
    if (!m.encrypted) return Promise.resolve(null);
    // An edit brings new ciphertext under the same id: it is decrypted again.
    const cacheKey = `${m.id}:${m.editedAt ?? ''}`;
    let p = this.plaintext.get(cacheKey);
    if (!p) {
      const encrypted = m.encrypted;
      p = this.exclusive(async () => {
        const { RoomId, DecryptionSettings, TrustRequirement } = this.lib;
        const room = new RoomId(e2eeRoomId(m.conversationId));
        await this.track([m.senderId]);
        const event = JSON.stringify({
          type: 'm.room.encrypted',
          event_id: `$${m.id}`,
          sender: e2eeUserId(m.senderId),
          origin_server_ts: Date.parse(m.createdAt),
          room_id: room.toString(),
          content: encrypted,
        });
        const read = async (trust: number) => {
          const dec = await this.machine.decryptRoomEvent(event, room, new DecryptionSettings(trust));
          const content = (JSON.parse(dec.event) as { content?: DecryptedContent }).content;
          return content && typeof content.body === 'string' ? content : null;
        };
        try {
          const content = await read(TrustRequirement.CrossSignedOrLegacy);
          return content && { content, unverifiedSender: false };
        } catch {
          try {
            const content = await read(TrustRequirement.Untrusted);
            return content && { content, unverifiedSender: true };
          } catch {
            return null;
          }
        }
      });
      this.plaintext.set(cacheKey, p);
    }
    return p;
  }

  // ---------- reactions ----------

  /** My reaction to a message, encrypted for everyone in the chat (an annotation pointing at it). */
  encryptReaction(conversationId: string, recipientIds: string[], messageId: string, emoji: string): Promise<MegolmContent> {
    return this.encryptContent(conversationId, recipientIds, {
      msgtype: REACTION_TYPE,
      body: emoji,
      'm.relates_to': { rel_type: 'm.annotation', event_id: `$${messageId}` },
    });
  }

  private reactions = new Map<string, Promise<string | null>>();

  /**
   * The emoji of one person's encrypted reaction, or null. Only accepted from a device that belongs to that
   * person (the library refuses a ciphertext passed off as someone else's), and only for this message.
   */
  decryptReaction(conversationId: string, messageId: string, userId: string, encrypted: MegolmContent): Promise<string | null> {
    const cacheKey = `${messageId}:${userId}:${encrypted.session_id}:${encrypted.ciphertext.slice(0, 32)}`;
    let p = this.reactions.get(cacheKey);
    if (!p) {
      p = this.exclusive(async () => {
        const { RoomId, DecryptionSettings, TrustRequirement } = this.lib;
        const room = new RoomId(e2eeRoomId(conversationId));
        await this.track([userId]);
        const event = JSON.stringify({
          type: 'm.room.encrypted',
          event_id: `$${messageId}~${userId}~${encrypted.ciphertext.slice(0, 16)}`,
          sender: e2eeUserId(userId),
          origin_server_ts: 0, // stable: the same ciphertext always describes the same event
          room_id: room.toString(),
          content: encrypted,
        });
        try {
          const dec = await this.machine.decryptRoomEvent(event, room, new DecryptionSettings(TrustRequirement.CrossSignedOrLegacy));
          const c = (JSON.parse(dec.event) as { content?: DecryptedContent }).content;
          const emoji = c?.body;
          const ok = c?.msgtype === REACTION_TYPE && c['m.relates_to']?.event_id === `$${messageId}` && typeof emoji === 'string' && emoji.length > 0 && emoji.length <= 16;
          return ok ? emoji : null;
        } catch {
          return null;
        }
      });
      // A key that arrives later should get another chance: failures are not kept.
      void p.then((v) => v === null && this.reactions.delete(cacheKey));
      this.reactions.set(cacheKey, p);
    }
    return p;
  }
}

const REACTION_TYPE = 'm.dijitalapp.reaction';

/** The QR code as a square grid of modules (1 = dark). */
function qrGrid(qr: CryptoModule.Qr): { size: number; modules: Uint8ClampedArray } {
  const modules = qr.toQrCode().renderIntoBuffer();
  return { size: Math.round(Math.sqrt(modules.length)), modules };
}
